import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const FAQ = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section className="w-full py-16 px-6" id="faq">
      <div className="max-w-4xl mx-auto">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="flex items-center justify-center gap-3 w-full group mb-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-foreground">
              Frequently Asked Questions
            </h2>
            <ChevronDown
              className={`h-6 w-6 text-foreground transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-8">
            <Tabs defaultValue="about" className="w-full">
              <TabsList className="w-full justify-center mb-8 flex-wrap h-auto gap-2 bg-white/50 backdrop-blur-sm border border-gray-200 p-2 rounded-xl">
                <TabsTrigger 
                  value="about" 
                  className="text-sm md:text-base px-6 py-3 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-assembly-purple data-[state=active]:to-assembly-pink data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-200"
                >
                  About Assembly
                </TabsTrigger>
                <TabsTrigger 
                  value="integrations" 
                  className="text-sm md:text-base px-6 py-3 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-assembly-purple data-[state=active]:to-assembly-pink data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-200"
                >
                  Integrations, Setup & Security
                </TabsTrigger>
                <TabsTrigger 
                  value="pricing" 
                  className="text-sm md:text-base px-6 py-3 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-assembly-purple data-[state=active]:to-assembly-pink data-[state=active]:text-white data-[state=active]:shadow-lg transition-all duration-200"
                >
                  Pricing, ROI & Getting Started
                </TabsTrigger>
              </TabsList>

              <TabsContent value="about" className="space-y-4">
                <Accordion type="single" collapsible className="space-y-4">
                  <AccordionItem value="item-1">
                    <AccordionTrigger>
                      <span className="text-left">What is Assembly?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Assembly is a B2B HR SaaS platform that helps organizations of every size — remote, hybrid, or on-site — automate and celebrate employee recognition, rewards, and AI-powered engagement analytics in one unified workspace. It integrates seamlessly with Slack, Microsoft Teams, and leading HRIS platforms to make culture measurable, effortless, and human.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-2">
                    <AccordionTrigger>
                      <span className="text-left">How does Assembly improve employee engagement?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Assembly turns recognition into a natural part of daily work. Employees can send shoutouts, give points, and redeem rewards directly in Slack or Teams. AI-driven analytics then help HR leaders measure participation, morale, and recognition trends in real time.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-3">
                    <AccordionTrigger>
                      <span className="text-left">What makes Assembly different from other recognition tools?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Most tools focus on one-off rewards. Assembly focuses on building recognition habits that last — with automation, integrations, and analytics that keep 90%+ of teams actively engaged month after month. It's designed for both emotional connection and measurable culture growth.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-4">
                    <AccordionTrigger>
                      <span className="text-left">Can small and mid-sized companies use Assembly?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Yes. Assembly scales from 10-person startups to global enterprises. Smaller teams can start with the Free Plan, while larger organizations can unlock advanced analytics, AI insights, and HRIS integrations as they grow.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-5">
                    <AccordionTrigger>
                      <span className="text-left">How does Assembly use AI in engagement?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Assembly uses Dora AI, its built-in analytics engine, to track engagement patterns, predict morale shifts, and highlight recognition trends. It helps HR and people leaders see what drives culture and retention — and take action instantly.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </TabsContent>

              <TabsContent value="integrations" className="space-y-4">
                <Accordion type="single" collapsible className="space-y-4">
                  <AccordionItem value="item-6">
                    <AccordionTrigger>
                      <span className="text-left">What tools does Assembly integrate with?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Assembly connects with the tools your teams already use — including Slack, Microsoft Teams, BambooHR, ADP, Workday, Rippling, and Google Workspace. These integrations ensure recognition and engagement happen directly within existing workflows, without extra logins or manual updates.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-7">
                    <AccordionTrigger>
                      <span className="text-left">Can Assembly integrate with our HRIS system?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Yes. Assembly integrates seamlessly with leading HRIS platforms to sync employee data, departments, milestones, and anniversaries — keeping your recognition and rewards programs fully automated.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-8">
                    <AccordionTrigger>
                      <span className="text-left">Is Assembly secure and compliant for enterprise use?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Absolutely. Assembly is SOC 2 Type II certified and GDPR compliant. It offers SSO, role-based access controls, and data encryption, ensuring complete data privacy and enterprise-grade protection.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-9">
                    <AccordionTrigger>
                      <span className="text-left">How fast can we set up Assembly?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Setup takes less than 15 minutes. You can connect your workspace, invite your team, and start sending recognition instantly. No technical expertise or lengthy onboarding required.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-10">
                    <AccordionTrigger>
                      <span className="text-left">Does Assembly support multi-location or hybrid teams?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Yes. Assembly is purpose-built for remote, hybrid, and global teams. Recognition flows naturally across locations and time zones — ensuring everyone feels included and valued, wherever they work.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </TabsContent>

              <TabsContent value="pricing" className="space-y-4">
                <Accordion type="single" collapsible className="space-y-4">
                  <AccordionItem value="item-11">
                    <AccordionTrigger>
                      <span className="text-left">Is Assembly free to use?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Yes — you can start with our Free Plan to explore recognition, rewards, and engagement analytics with no credit card required. When you're ready, upgrade to unlock advanced features and integrations.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-12">
                    <AccordionTrigger>
                      <span className="text-left">How much does Assembly cost?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Assembly offers flexible pricing based on your team size and needs. Smaller teams can use the Free Plan, while mid-sized and enterprise organizations can choose scalable packages with AI analytics, HRIS integrations, and global reward options.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-13">
                    <AccordionTrigger>
                      <span className="text-left">What kind of results can we expect with Assembly?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <div className="text-muted-foreground leading-relaxed space-y-2">
                        <p>Companies using Assembly typically see:</p>
                        <ul className="list-disc pl-6 space-y-1">
                          <li>70% increase in engagement within 90 days</li>
                          <li>2x more peer recognition activity</li>
                          <li>60% drop in voluntary attrition</li>
                          <li>3x faster adoption than other tools</li>
                        </ul>
                        <p className="mt-2">These metrics prove that recognition-driven cultures perform better — and stay longer.</p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-14">
                    <AccordionTrigger>
                      <span className="text-left">How does Assembly help HR leaders measure ROI?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <p className="text-muted-foreground leading-relaxed">
                        Assembly's analytics dashboard visualizes engagement rates, recognition frequency, and reward trends. It helps HR leaders connect recognition programs to real outcomes like productivity, retention, and morale.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-15">
                    <AccordionTrigger>
                      <span className="text-left">How do I get started with Assembly?</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-4">
                      <div className="text-muted-foreground leading-relaxed space-y-2">
                        <p>You can get started in three simple steps:</p>
                        <ol className="list-decimal pl-6 space-y-1">
                          <li>Connect your workspace (Slack, Teams, or HRIS).</li>
                          <li>Invite your team and launch your first recognition.</li>
                          <li>Track engagement and rewards in real time.</li>
                        </ol>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </TabsContent>
            </Tabs>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </section>
  );
};

export default FAQ;
